/**
 * Scroll Utilities
 * Shared utilities for scroll positioning and responsive breakpoints
 */

class ScrollUtils {
  /**
   * Get breakpoint value from CSS custom property
   * @param {string} breakpoint - Breakpoint name: 'xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl'
   * @returns {number} Breakpoint value in pixels
   */
  static getBreakpointValue(breakpoint) {
    const cssValue = getComputedStyle(document.documentElement)
      .getPropertyValue(`--breakpoint-${breakpoint}`)
      .trim();
    
    if (!cssValue) {
      console.warn(`ScrollUtils: CSS variable --breakpoint-${breakpoint} not found`);
      return 0;
    }
    
    return parseInt(cssValue, 10);
  }

  /**
   * Check if current viewport is at or above a breakpoint
   * @param {string} breakpoint - Breakpoint name: 'xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl'
   * @returns {boolean}
   */
  static isBreakpointUp(breakpoint) {
    const width = this.getBreakpointValue(breakpoint);
    if (width === 0) return false;
    return window.innerWidth >= width;
  }

  /**
   * Check if current viewport is below a breakpoint
   * @param {string} breakpoint - Breakpoint name: 'xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl'
   * @returns {boolean}
   */
  static isBreakpointDown(breakpoint) {
    const width = this.getBreakpointValue(breakpoint);
    if (width === 0) return false;
    return window.innerWidth < width;
  }

  /**
   * Get the current breakpoint name
   * @returns {string} Current breakpoint: 'xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl'
   */
  static getCurrentBreakpoint() {
    const width = window.innerWidth;
    
    const breakpoints = ['xxxl', 'xxl', 'xl', 'lg', 'md', 'sm', 'xs'];
    
    for (const bp of breakpoints) {
      const bpWidth = this.getBreakpointValue(bp);
      if (bpWidth > 0 && width >= bpWidth) {
        return bp;
      }
    }
    
    return 'xs';
  }

  /**
   * Get the current header offset height from CSS variable
   * Falls back to calculating if variable not set
   * @returns {number} Header offset in pixels
   */
  static getHeaderOffset() {
    // Try to get the CSS variable first (set by header JavaScript)
    const stickyOffset = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--sticky-offset'),
      10
    );
    
    if (!isNaN(stickyOffset) && stickyOffset > 0) {
      return stickyOffset;
    }
    
    // Fallback: Calculate from actual header
    const header = document.querySelector('[data-header]');
    return header ? header.offsetHeight : 100; // 100px fallback
  }

  /**
   * Watch for changes to the header offset and call a callback
   * Useful for components that need to react to breakpoint changes
   * @param {Function} callback - Called with { offset, breakpoint }
   * @returns {Function} - Cleanup function to stop watching
   */
  static watchHeaderOffset(callback) {
    const handler = (event) => {
      callback(event.detail);
    };
    
    // Listen to custom event from header
    window.addEventListener('header:offset-changed', handler);
    
    // Return cleanup function
    return () => {
      window.removeEventListener('header:offset-changed', handler);
    };
  }
  
  /**
   * Scroll to an element, accounting for fixed header
   * @param {HTMLElement|string} element - Element or selector to scroll to
   * @param {Object} options - Scroll options
   * @param {number} options.extraOffset - Additional offset in pixels (default: 20)
   * @param {string} options.behavior - Scroll behavior 'smooth' or 'auto' (default: 'smooth')
   */
  static scrollToElement(element, options = {}) {
    const target = typeof element === 'string' 
      ? document.querySelector(element) 
      : element;
    
    if (!target) {
      console.warn('ScrollUtils: Target element not found');
      return;
    }
    
    const {
      extraOffset = 20,
      behavior = 'smooth'
    } = options;
    
    // Calculate offset: header height + extra spacing
    const headerOffset = this.getHeaderOffset();
    const totalOffset = headerOffset + extraOffset;
    
    // Get target position
    const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - totalOffset;
    
    // Smooth scroll
    window.scrollTo({
      top: targetPosition,
      behavior: behavior
    });
  }
  
  /**
   * Get the scroll position for an element (without actually scrolling)
   * @param {HTMLElement|string} element - Element or selector
   * @param {number} extraOffset - Additional offset in pixels (default: 20)
   * @returns {number} - The Y position to scroll to
   */
  static getScrollPosition(element, extraOffset = 20) {
    const target = typeof element === 'string' 
      ? document.querySelector(element) 
      : element;
    
    if (!target) {
      console.warn('ScrollUtils: Target element not found');
      return 0;
    }
    
    const headerOffset = this.getHeaderOffset();
    const totalOffset = headerOffset + extraOffset;
    
    return target.getBoundingClientRect().top + window.pageYOffset - totalOffset;
  }
}

// Make available globally
window.ScrollUtils = ScrollUtils;
